/**
 * BuyerRatings Screen
 *
 * Lets the user assign credit ratings (e.g., AA, A, BBB, NIG/NR) to each
 * buyer parent. Ratings drive the concentration limit percentages used in
 * the ConcentrationLimits calculation downstream.
 *
 * Interaction model:
 *  - Drag-and-drop: user drags a RatingPill from the toolbar onto a buyer row.
 *  - AI classification: per-buyer button calls `classifyBuyer` (Weaviate-backed)
 *    which returns a suggested rating, confidence level, and reasoning.
 *    The user can accept or ignore the suggestion.
 *  - "Load Default Ratings" bulk-imports ratings from a pre-existing dataset.
 *
 * Data flow:
 *  - Depends on BuyerParent having been calculated first (needs `buyerParent` data).
 *  - Reads `project.ratingScale` to determine available rating tiers.
 *  - The lowest-ranked rating in ratingScale is used as the default for
 *    unassigned buyers.
 *  - Buyer list is sorted by total_debit descending (largest exposure first).
 *  - Ratings are persisted in `project.buyerRatings` (auto-saved to backend).
 */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getBuyerAr, classifyBuyer, loadDefaultRatings } from "Redux/data/actions";

const PILL_STYLES = {
  AA: { backgroundColor: "#430098", color: "#fff" },
  A: { backgroundColor: "#79589F", color: "#fff" },
  BBB: { backgroundColor: "#E8DFF0", color: "#430098" },
  "NIG/NR": { backgroundColor: "#F4F0F8", color: "#7A7490", border: "1px solid #E2DDE8" },
};

const getPillStyle = (rating) => PILL_STYLES[rating] || PILL_STYLES["NIG/NR"];

const CONFIDENCE_STYLE = {
  high: { backgroundColor: "#D8F5E8", color: "#1B7A4A" },
  medium: { backgroundColor: "#FFF3D6", color: "#8A6A17" },
  low: { backgroundColor: "#FDE2E2", color: "#B22525" },
};

// Draggable rating badge. The HTML drag-and-drop API carries the rating
// string via dataTransfer so BuyerRow drop targets can read it.
const RatingPill = ({ rating, onDragStart }) => (
  <span
    className="tag is-medium"
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData("rating", rating);
      onDragStart(rating);
    }}
    style={{
      cursor: "grab",
      marginRight: "0.5rem",
      userSelect: "none",
      fontWeight: 700,
      borderRadius: "6px",
      letterSpacing: "0.02em",
      ...getPillStyle(rating),
    }}
  >
    {rating}
  </span>
);

// Table row that is both a drop target for rating pills and hosts
// the AI classify button. When a classification result exists, an
// expandable sub-row shows the suggested rating, confidence, reasoning,
// and an "Accept" button to apply it.
const BuyerRow = ({ buyer, currentRating, defaultRating, onDrop, onClassify, classifyState }) => {
  const [dragOver, setDragOver] = useState(false);
  const isClassifying = classifyState?.loading;
  const classResult = classifyState?.result;

  return (
    <>
      <tr
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const rating = e.dataTransfer.getData("rating");
          if (rating) onDrop(buyer.Buyer_Parent_Name, rating);
        }}
        style={{
          backgroundColor: dragOver ? "var(--ev-purple-ghost)" : undefined,
          transition: "background-color 0.15s",
        }}
      >
        <td>
          <div className="tag is-info is-light">{buyer.Buyer_Parent_Name}</div>
        </td>
        <td style={{ textAlign: "right" }}>
          {new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Math.round(buyer.total_ar))}
        </td>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            {currentRating ? (
              <span
                className="tag"
                style={{
                  fontWeight: 700,
                  borderRadius: "6px",
                  ...getPillStyle(currentRating),
                }}
              >
                {currentRating}
                <button
                  className="delete is-small"
                  onClick={() => onDrop(buyer.Buyer_Parent_Name, null)}
                  style={{ marginLeft: "0.25rem" }}
                />
              </span>
            ) : (
              <span
                className="tag"
                style={{
                  opacity: 0.4,
                  borderRadius: "6px",
                  ...getPillStyle(defaultRating),
                }}
              >
                {defaultRating}
              </span>
            )}
          </div>
        </td>
        <td style={{ textAlign: "center" }}>
          <button
            className={`button is-small ${isClassifying ? "is-loading" : ""}`}
            onClick={() => onClassify(buyer.Buyer_Parent_Name)}
            disabled={isClassifying}
            style={{ borderRadius: "6px", minWidth: "32px" }}
            title="Classify with AI"
          >
            <span className="icon" style={{ fontSize: "0.8rem" }}>
              <i className="fa-duotone fa-wand-magic-sparkles" />
            </span>
          </button>
        </td>
      </tr>
      {classResult && (
        <tr>
          <td
            colSpan="4"
            style={{
              padding: "0.4rem 0.75rem",
              backgroundColor: "var(--ev-purple-ghost)",
              borderTop: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <span
                className="tag"
                style={{
                  fontWeight: 700,
                  borderRadius: "6px",
                  ...getPillStyle(classResult.rating),
                }}
              >
                {classResult.rating || "?"}
              </span>
              <span
                className="tag"
                style={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  borderRadius: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  ...(CONFIDENCE_STYLE[classResult.confidence] || {}),
                }}
              >
                {classResult.confidence} confidence
              </span>
              {classResult.rating && (
                <button
                  className="button is-small"
                  style={{ borderRadius: "6px", fontSize: "0.7rem" }}
                  onClick={() => {
                    onDrop(buyer.Buyer_Parent_Name, classResult.rating);
                  }}
                >
                  Accept
                </button>
              )}
              <span
                style={{
                  fontSize: "0.72rem",
                  color: "var(--ev-text-muted)",
                  flex: 1,
                }}
              >
                {classResult.reasoning}
              </span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// -------------------------------------------------------------
// -------------------------------------------------------------
const BuyerRatings = () => {
  const dispatch = useDispatch();
  const [classifyStates, setClassifyStates] = useState({});
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  const { buyerAr } = useSelector((state) => state.data);
  const { hasMaster, ratingScale, buyerRatings } = useSelector(
    (state) => state.project
  );

  useEffect(() => {
    if (hasMaster && !buyerAr) {
      dispatch(getBuyerAr());
    }
  }, [hasMaster, buyerAr, dispatch]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  if (!buyerAr)
    return (
      <div className="notification is-info is-light">
        Loading buyer data...
      </div>
    );

  const ratings = (ratingScale || []).map((r) => r.rating).filter((r) => r);
  const defaultRating = ratings.length > 0 ? ratings[ratings.length - 1] : "NIG/NR";

  const buyers = [...buyerAr].sort(
    (a, b) => b.total_ar - a.total_ar
  );

  const handleDrop = (buyerName, rating) => {
    const updated = { ...buyerRatings };
    if (rating === null || rating === defaultRating) {
      delete updated[buyerName];
    } else {
      updated[buyerName] = rating;
    }
    dispatch({ type: "SET_BUYER_RATINGS", buyerRatings: updated });
  };

  const handleClassify = (companyName) => {
    setClassifyStates((prev) => ({
      ...prev,
      [companyName]: { loading: true, result: null },
    }));
    dispatch(
      classifyBuyer(companyName, (result) => {
        setClassifyStates((prev) => ({
          ...prev,
          [companyName]: { loading: false, result },
        }));
      })
    );
  };

  return (
    <div>
      <div className="box">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, marginRight: "0.5rem" }}>
            Drag a rating to assign:
          </span>
          {ratings.map((r) => (
            <RatingPill key={r} rating={r} onDragStart={() => {}} />
          ))}
          <button
            className={`button is-small ${loadingDefaults ? "is-loading" : ""}`}
            onClick={() => {
              setLoadingDefaults(true);
              dispatch(loadDefaultRatings(() => setLoadingDefaults(false)));
            }}
            style={{ borderRadius: "6px", marginLeft: "0.5rem" }}
          >
            <span className="icon" style={{ fontSize: "0.8rem" }}>
              <i className="fa-duotone fa-file-import" />
            </span>
            <span>Load Default Ratings</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="table is-fullwidth is-striped is-bordered is-small-table">
          <thead>
            <tr className="header-row">
              <th className="is-light">
                <div className="tags has-addons">
                  <span className="tag is-warning is-light">Buyer Parent Name</span>
                </div>
              </th>
              <th className="is-light">
                <div className="tags has-addons">
                  <span className="tag is-warning is-light">Total AR</span>
                </div>
              </th>
              <th className="is-light">
                <div className="tags has-addons">
                  <span className="tag is-warning is-light">Rating</span>
                </div>
              </th>
              <th className="is-light" style={{ width: "50px" }}>
                <div className="tags has-addons">
                  <span className="tag is-warning is-light">AI</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {buyers.map((buyer) => (
              <BuyerRow
                key={buyer.Buyer_Parent_Name}
                buyer={buyer}
                currentRating={buyerRatings[buyer.Buyer_Parent_Name] || null}
                defaultRating={defaultRating}
                onDrop={handleDrop}
                onClassify={handleClassify}
                classifyState={classifyStates[buyer.Buyer_Parent_Name]}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuyerRatings;
